import * as React from "react";
import { useTheme } from "@mui/material/styles";
import {
  Avatar,
  Box,
  CircularProgress,
  Tabs,
  Tab,
  Stack,
  Typography,
  Rating,
  IconButton,
  Menu,
  MenuItem,
  Alert,
  Divider,
  Chip,
  Grid,
  Skeleton,
  useMediaQuery,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import {
  useGetTalentByIdQuery,
  useGetTalentTotalJobSuccessRateByIdQuery,
  useGetTalentTotalJobHiredByIdQuery,
  useGetTalentTotalEarnedByIdQuery,
} from "../../services/talent.service";
import { useTitle } from "../../hooks/useTitle";
import PersonPinCircleIcon from "@mui/icons-material/PersonPinCircle";
import EmailIcon from "@mui/icons-material/Email";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import { Talent } from "../../types/users";
import TalentProfilePanel from "./TalentProfilePanel";
import TalentJobsPanel from "./TalentJobsPanel";
import TalentFundsPanel from "./TalentFundsPanel";
import { formatCash, formatMoney } from "../../utils/helper";
import { MoreVert } from "@mui/icons-material";
import { CardBase, OrangeLinearProgress } from "../../components/global";

const MoreMenu = ({ talent }: { talent?: Talent; }) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <div>
      <IconButton
        id="basic-button"
        aria-controls={open ? "basic-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        onClick={handleClick}
        sx={{ ml: 1 }}
      >
        <MoreVert />
      </IconButton>
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "basic-button",
        }}
      >
        <MenuItem onClick={() => navigate(`/talents/${talent?.id}/reviews`)}>
          Reviews
        </MenuItem>
      </Menu>
    </div>
  );
};

const ProfileStat = ({
  label,
  value,
  isLoading,
  onClick,
}: {
  label: string;
  value: string;
  isLoading?: boolean;
  onClick?: () => void;
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Box
      onClick={onClick}
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        py: isMobile ? 1 : 1.5,
        px: isMobile ? 1 : 2.5,
        alignItems: "center",
        bgcolor: "background.paper",
        minWidth: isMobile ? 0 : 100,
        width: "100%",
        cursor: onClick ? "pointer" : "default",
        transition: "all 0.2s",
        "&:hover": onClick
          ? {
            borderColor: "#F4A259",
            boxShadow: "0 2px 8px rgba(244, 162, 89, 0.2)",
          }
          : {},
      }}
    >
      {isLoading ? (
        <Box sx={{ textAlign: "center", py: 1 }}>
          <CircularProgress size={isMobile ? 16 : 20} />
        </Box>
      ) : (
        <Typography
          textAlign={"center"}
          fontSize={isMobile ? 14 : 18}
          fontWeight={600}
          letterSpacing={0.5}
          sx={{ color: "#F4A259" }}
          noWrap
        >
          {value}
        </Typography>
      )}
      <Typography
        textAlign={"center"}
        fontSize={isMobile ? 10 : 12}
        color="text.secondary"
        sx={{ mt: 0.5 }}
        noWrap
      >
        {label}
      </Typography>
    </Box>
  );
};

const ProfileMeta = ({
  icon,
  value,
  onClick,
}: {
  icon: React.ReactNode;
  value: string;
  onClick?: () => void;
}) => (
  <Chip
    icon={icon as React.ReactElement}
    label={value}
    size="small"
    variant="outlined"
    onClick={onClick}
    sx={{
      mr: 1,
      mb: 1,
      color: "text.secondary",
      borderColor: "divider",
      cursor: onClick ? "pointer" : "default",
      "& .MuiChip-icon": {
        color: "text.secondary",
      },
    }}
  />
);

const ProfileHeader = ({ data }: { data?: Talent; }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const talentId = data?.id;

  const {
    data: dataTotalJobSuccessRate,
    isLoading: isLoadingTotalJobSuccessRate,
  } = useGetTalentTotalJobSuccessRateByIdQuery(talentId!, {
    skip: !talentId,
  });

  const { data: dataTotalJobHired, isLoading: isLoadingTotalJobHired } =
    useGetTalentTotalJobHiredByIdQuery(talentId!, {
      skip: !talentId,
    });

  const { data: dataTotalEarned, isLoading: isLoadingTotalEarned } =
    useGetTalentTotalEarnedByIdQuery(talentId!, {
      skip: !talentId,
    });

  const isLoading = isLoadingTotalJobSuccessRate || isLoadingTotalJobHired || isLoadingTotalEarned;

  if (isLoading && !data)
    return (
      <CardBase useGlassCard={false} sx={{ mb: { xs: 2, sm: 3 } }}>
        <Stack direction="column" spacing={2}>
          <Skeleton variant="text" sx={{ width: "30%", height: 40 }} />
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Skeleton variant="rectangular" width={100} height={32} sx={{ borderRadius: 1 }} />
            <Skeleton variant="rectangular" width={120} height={32} sx={{ borderRadius: 1 }} />
            <Skeleton variant="rectangular" width={150} height={32} sx={{ borderRadius: 1 }} />
          </Stack>
        </Stack>
      </CardBase>
    );

  return (
    <CardBase
      useGlassCard={false}
      sx={{ mb: { xs: 2, sm: 3 }, p: { xs: 1.5, sm: 2.5 } }}
      headerActions={<MoreMenu talent={data} />}
    >
      <Stack
        direction="row"
        alignItems="flex-start"
        spacing={{ xs: 1.5, sm: 2 }}
        sx={{ width: "100%" }}
      >
        <Avatar
          variant="square"
          sx={{
            bgcolor: "#F4A259",
            width: { xs: 60, sm: 100 },
            height: { xs: 60, sm: 100 },
            fontSize: { xs: 24, sm: 40 },
            flexShrink: 0,
          }}
          src={
            (!!data?.photo?.length && data?.photo[0].original_url) ||
            undefined
          }
        >
          {data?.full_name?.charAt(0)?.toUpperCase()}
        </Avatar>
        <Stack direction="column" flex={1} spacing={{ xs: 1, sm: 2 }} sx={{ minWidth: 0 }}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={{ xs: 0.5, sm: 1 }}
            flexWrap="wrap"
          >
            <Typography
              variant="h5"
              sx={{
                color: "#F4A259",
                fontWeight: 600,
                fontSize: { xs: "1rem", sm: "1.5rem" },
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                flex: 1,
                minWidth: 0,
              }}
            >
              {data?.full_name}
            </Typography>
            <Rating
              value={data?.total_rating || 0}
              readOnly
              size="small"
              sx={{ flexShrink: 0 }}
            />
          </Stack>

          <Stack
            direction="row"
            alignItems="center"
            flexWrap="wrap"
            spacing={{ xs: 0.5, sm: 1 }}
          >
            <ProfileMeta
              icon={<PersonPinCircleIcon />}
              value={`${data?.city || "Unknown City"}, ${data?.state || "Unknown State"
                }`}
            />
            <ProfileMeta
              icon={<EmailIcon />}
              value={data?.email || "No Email"}
              onClick={() => {
                if (data?.email) {
                  window.location.href = `mailto:${data.email}`;
                }
              }}
            />
            <ProfileMeta
              icon={<PhoneAndroidIcon />}
              value={data?.phone_number || "No Phone"}
              onClick={() => {
                if (data?.phone_number) {
                  window.location.href = `TEL:${data.phone_number}`;
                }
              }}
            />
          </Stack>
          <Grid container spacing={{ xs: 0.75, sm: 1 }} sx={{ mt: { xs: 0, sm: 0 } }}>
            <Grid item xs={4} sm="auto">
              <ProfileStat
                label="Balance"
                value={
                  !!data?.balance_decrypt
                    ? `$${formatMoney(data?.balance_decrypt || 0)}`
                    : `$0`
                }
              />
            </Grid>
            <Grid item xs={4} sm="auto">
              <ProfileStat
                label="Rating"
                value={`${+Number(data?.total_rating || 0).toFixed(1)}`}
                onClick={() => navigate(`/talents/${data?.id}/reviews`)}
              />
            </Grid>
            <Grid item xs={4} sm="auto">
              <ProfileStat
                label="Job Success"
                value={`${+Number(dataTotalJobSuccessRate?.total || 0).toFixed(
                  2
                )}%`}
                isLoading={isLoadingTotalJobSuccessRate}
              />
            </Grid>
            <Grid item xs={4} sm="auto">
              <ProfileStat
                label="Jobs"
                value={`${dataTotalJobHired?.total || 0}`}
                isLoading={isLoadingTotalJobHired}
              />
            </Grid>
            <Grid item xs={4} sm="auto">
              <ProfileStat
                label="Total Earned"
                value={`$${formatCash(Number(dataTotalEarned?.total || 0))}`}
                isLoading={isLoadingTotalEarned}
              />
            </Grid>
          </Grid>
        </Stack>
      </Stack>
    </CardBase>
  );
};

const TalentDetailPage = () => {
  const { talentId } = useParams();
  const theme = useTheme();
  const [tab, setTab] = React.useState("profile");

  const {
    data,
    isFetching: fetching,
    isError,
    error,
  } = useGetTalentByIdQuery(parseInt(talentId!), {
    skip: !talentId,
  });

  useTitle(
    `Talent #${data?.id}`,
    `${data?.first_name} ${data?.last_name} | Talent`
  );

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setTab(newValue);
  };

  //@ts-ignore
  if (isError && error?.status == 404) {
    return (
      <CardBase useGlassCard={false}>
        <Alert severity="error">
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
            ERROR
          </Typography>
          Talent not found or deleted
        </Alert>
      </CardBase>
    );
  }

  function a11yProps(index: string) {
    return {
      id: `talent-tab-${index}`,
      "aria-controls": `talent-tabpanel-${index}`,
    };
  }

  function TabPanel(props: {
    children?: React.ReactNode;
    index: string;
    value: string;
  }) {
    const { children, value, index, ...other } = props;

    return (
      <div
        role="tabpanel"
        hidden={value !== index}
        id={`talent-tabpanel-${index}`}
        aria-labelledby={`talent-tab-${index}`}
        {...other}
      >
        {value === index && (
          <Box sx={{ p: { xs: 2, sm: 3 } }}>{children}</Box>
        )}
      </div>
    );
  }

  return (
    <Box>
      {fetching && <OrangeLinearProgress position="relative" sx={{ mb: 2 }} />}
      {!fetching && <ProfileHeader data={data} />}
      <CardBase useGlassCard={false} sx={{ p: 0, mt: 3 }}>
        <Tabs
          value={tab}
          onChange={handleTabChange}
          textColor="inherit"
          variant="fullWidth"
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            "& .MuiTab-root": {
              fontWeight: 500,
              color: "text.secondary",
              "&.Mui-selected": {
                color: "#F4A259 !important",
                fontWeight: 600,
              },
            },
            "& .MuiTabs-indicator": {
              backgroundColor: "#F4A259",
            },
            "& .MuiTabs-scrollButtons": {
              color: "#F4A259",
            },
          }}
        >
          <Tab label="Profile" value={"profile"} {...a11yProps("profile")} />
          <Tab label="Jobs" value={"jobs"} {...a11yProps("jobs")} />
          <Tab label="Finances" value={"funds"} {...a11yProps("funds")} />
        </Tabs>
        <TabPanel value={tab} index="profile">
          <TalentProfilePanel data={data} />
        </TabPanel>
        <TabPanel value={tab} index="jobs">
          <TalentJobsPanel data={data} />
        </TabPanel>
        <TabPanel value={tab} index="funds">
          <TalentFundsPanel data={data} />
        </TabPanel>
      </CardBase>
    </Box>
  );
};
export default TalentDetailPage;
